// Shopify theme sync marker: listing-rewrite-2026-06-21.
import { getFreshLocationIfAlreadyGranted } from '@theme/so-atlanta-location';

const MAPBOX_GL_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.js';
const MAPBOX_GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.css';

/** @returns {Promise<void>} */
function loadMapbox() {
  if (window.mapboxgl) return Promise.resolve();

  const cssLink = document.createElement('link');
  cssLink.rel = 'stylesheet';
  cssLink.href = MAPBOX_GL_CSS;
  document.head.appendChild(cssLink);

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = MAPBOX_GL_JS;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Mapbox load failed'));
    document.head.appendChild(script);
  });
}

/** @param {string|undefined} url */
function isStandardStyleUrl(url) {
  return typeof url === 'string' && url.includes('mapbox://styles/mapbox/standard');
}

class VenueInfoMap extends HTMLElement {
  /** @type {any} */
  #map = null;

  /** @type {any} */
  #marker = null;

  #alive = false;

  static get observedAttributes() {
    return ['data-lat', 'data-lng'];
  }

  /** @type {() => void} */
  #onResize = () => {
    try {
      this.#map?.resize();
    } catch (_) {
      /* noop */
    }
  };

  connectedCallback() {
    this.#alive = true;
    window.addEventListener('resize', this.#onResize);
    this.#init();
  }

  disconnectedCallback() {
    this.#alive = false;
    window.removeEventListener('resize', this.#onResize);
    if (this.#map) {
      try {
        this.#map.remove();
      } catch (_) {
        /* noop */
      }
      this.#map = null;
      this.#marker = null;
    }
  }

  attributeChangedCallback() {
    this.#syncLocationFromAttributes();
  }

  setLocation(latitude, longitude) {
    if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return;
    this.dataset.lat = String(latitude);
    this.dataset.lng = String(longitude);
    this.#syncLocationFromAttributes();
  }

  async #init() {
    const token = (this.dataset.accessToken || '').trim();
    const { lat, lng, hasCoords } = this.#readCoordinates();
    const zoom = Number.parseFloat(String(this.dataset.zoom ?? '15')) || 15;
    const styleUrl = (this.dataset.styleUrl || '').trim() || 'mapbox://styles/mapbox/standard';

    const container = this.querySelector('.venue-info-map__canvas');
    const fallback = this.querySelector('.venue-info-map__fallback');

    if (!hasCoords) {
      if (container instanceof HTMLElement) container.setAttribute('hidden', '');
      if (fallback instanceof HTMLElement) {
        fallback.removeAttribute('hidden');
        fallback.textContent =
          this.dataset.placeholderNoCoords || 'Add latitude and longitude to show the map.';
      }
      return;
    }

    if (!token) {
      if (container instanceof HTMLElement) container.setAttribute('hidden', '');
      if (fallback instanceof HTMLElement) {
        fallback.removeAttribute('hidden');
        fallback.replaceChildren();
        const dest = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        const a = document.createElement('a');
        a.href = dest;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.className = 'link';
        a.textContent = this.dataset.placeholderNoToken || 'Open directions';
        fallback.appendChild(a);
      }
      return;
    }

    if (fallback instanceof HTMLElement) {
      fallback.setAttribute('hidden', '');
      fallback.replaceChildren();
    }
    if (container instanceof HTMLElement) container.removeAttribute('hidden');

    try {
      await loadMapbox();
      if (!this.#alive || !window.mapboxgl || !(container instanceof HTMLElement)) return;

      const mapboxgl = window.mapboxgl;
      mapboxgl.accessToken = token;

      const mapOptions = {
        container,
        style: styleUrl,
        center: [lng, lat],
        zoom,
        pitch: 45,
        attributionControl: true,
      };

      if (isStandardStyleUrl(styleUrl)) {
        mapOptions.config = {
          basemap: {
            lightPreset: 'day',
            theme: 'monochrome',
            font: 'Source Serif Pro',
          },
        };
      }

      container.innerHTML = '';

      this.#map = new mapboxgl.Map(mapOptions);
      this.#map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
      this.#marker = new mapboxgl.Marker({ color: '#111' }).setLngLat([lng, lat]).addTo(this.#map);

      this.#map.on('load', () => {
        if (!this.#alive || !this.#map) return;
        if (isStandardStyleUrl(styleUrl)) {
          try {
            this.#map.setConfigProperty('basemap', 'lightPreset', 'day');
            this.#map.setConfigProperty('basemap', 'theme', 'monochrome');
            this.#map.setConfigProperty('basemap', 'font', 'Source Serif Pro');
          } catch (e) {
            console.warn('Venue info map: basemap config skipped', e);
          }
        }
        this.#syncLocationFromAttributes();
        this.#map.resize();
      });
    } catch (e) {
      console.error('Venue info map:', e);
      if (container instanceof HTMLElement) container.setAttribute('hidden', '');
      if (fallback instanceof HTMLElement) {
        fallback.removeAttribute('hidden');
        fallback.textContent = this.dataset.loadError || 'Map could not be loaded.';
      }
    }
  }

  #readCoordinates() {
    const lat = Number.parseFloat(String(this.dataset.lat ?? ''));
    const lng = Number.parseFloat(String(this.dataset.lng ?? ''));
    return {
      lat,
      lng,
      hasCoords: !Number.isNaN(lat) && !Number.isNaN(lng),
    };
  }

  #syncLocationFromAttributes() {
    if (!this.#map) return;
    const { lat, lng, hasCoords } = this.#readCoordinates();
    if (!hasCoords) return;
    try {
      this.#map.setCenter([lng, lat]);
      this.#marker?.setLngLat([lng, lat]);
    } catch (_) {
      /* noop */
    }
  }
}

if (!customElements.get('venue-info-map')) {
  customElements.define('venue-info-map', VenueInfoMap);
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function firstValue(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return '';
}

function textHtml(value) {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => typeof item === 'string' ? item : firstValue(item?.label, item?.name, item?.value))
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return items.length ? `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : '';
  }
  if (value && typeof value === 'object') return '';
  const text = String(value == null ? '' : value).trim();
  if (!text) return '';
  return text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${escapeHtml(part).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function mapsUrl(name, address, lat, lng) {
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  const query = [name, address].filter(Boolean).join(' ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
}

function cleanAddress(value) {
  const address = String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  if (!address) return '';
  const zipCityState = address.match(/^(.+?,\s*([^,]+),\s*([A-Z]{2})\s+\d{5}(?:-\d{4})?),\s*\2,\s*\3$/i);
  if (zipCityState) return zipCityState[1].trim();
  return address.replace(/,\s*([^,]+),\s*([A-Z]{2})\s*$/, (match, city, state, offset, full) => {
    const prefix = full.slice(0, offset);
    const previousCityState = new RegExp(`,\\s*${escapeRegExp(city)}\\s*,\\s*${escapeRegExp(state)}(?:\\s+\\d{5}(?:-\\d{4})?)?\\s*$`, 'i');
    return previousCityState.test(prefix) ? '' : match;
  }).trim();
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderContact(business) {
  const items = [];
  const phone = firstValue(business.phone, business.phoneNumber, business.phone_number);
  const website = firstValue(business.websiteUrl, business.website_url, business.website);
  const instagramUrl = firstValue(business.instagramUrl, business.instagram_url);
  const instagramHandle = String(firstValue(business.instagramHandle, business.instagram_handle)).replace(/^@+/, '').trim();
  const tiktokUrl = firstValue(business.tiktokUrl, business.tiktok_url);
  const tiktokHandle = String(firstValue(business.tiktokHandle, business.tiktok_handle)).replace(/^@+/, '').trim();

  if (phone) items.push(`<li><a href="tel:${escapeHtml(String(phone).replace(/[^+\d]/g, ''))}">${escapeHtml(phone)}</a></li>`);
  if (website) items.push(`<li><a href="${escapeHtml(website)}" target="_blank" rel="noopener noreferrer">Website</a></li>`);
  if (instagramUrl || instagramHandle) {
    const href = instagramUrl || `https://www.instagram.com/${encodeURIComponent(instagramHandle)}/`;
    items.push(`<li><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">Instagram${instagramHandle ? ` @${escapeHtml(instagramHandle)}` : ''}</a></li>`);
  }
  if (tiktokUrl || tiktokHandle) {
    const href = tiktokUrl || `https://www.tiktok.com/@${encodeURIComponent(tiktokHandle)}`;
    items.push(`<li><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">TikTok${tiktokHandle ? ` @${escapeHtml(tiktokHandle)}` : ''}</a></li>`);
  }

  return items.length ? `<ul>${items.join('')}</ul>` : '';
}

function findInfoCard(root, title) {
  return [...root.querySelectorAll('[class*="ai-venue-info__card-"]')].find((card) => {
    const heading = card.querySelector('[class*="ai-venue-info__card-title-"]');
    return heading?.textContent?.trim().toLowerCase() === title.toLowerCase();
  });
}

function setInfoCard(root, title, html) {
  const card = findInfoCard(root, title);
  if (!(card instanceof HTMLElement)) return;
  const content = card.querySelector('[class*="ai-venue-info__card-content-"]');
  if (!(content instanceof HTMLElement) || !html) {
    card.hidden = true;
    return;
  }
  content.innerHTML = html;
  card.hidden = false;
}

function distanceMiles(lat1, lon1, lat2, lon2) {
  const radiusMiles = 3958.8;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return radiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatMiles(miles) {
  if (!Number.isFinite(miles)) return '';
  if (miles < 0.1) return 'Less than 0.1 mi';
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

function renderHeaderAddress(root, business, address, href, lat, lng) {
  const title = root.querySelector('[class*="ai-venue-info__title-"]');
  if (!(title instanceof HTMLElement) || !address) return;

  const existing = root.querySelector('[data-venue-compat-address]');
  if (existing) existing.remove();

  const line = document.createElement('p');
  line.dataset.venueCompatAddress = 'true';
  line.style.margin = '0 0 12px';

  const link = document.createElement('a');
  link.href = href || mapsUrl(business.name, address, lat, lng) || '#';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = address;
  line.appendChild(link);

  const distance = document.createElement('span');
  line.appendChild(distance);
  title.insertAdjacentElement('afterend', line);

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !canShowDistanceFor(business)) return;
  getFreshLocationIfAlreadyGranted({ timeout: 8000 }).then((location) => {
    if (!location) return;
    const miles = distanceMiles(location.latitude, location.longitude, lat, lng);
    distance.textContent = ` (${formatMiles(miles)} away)`;
  });
}

function canShowDistanceFor(business) {
  const type = String(firstValue(business?.listingType, business?.type, 'PLACE')).toUpperCase();
  if (type !== 'SERVICE') return true;

  const service = business?.service && typeof business.service === 'object' ? business.service : {};
  const noAddressReason = String(firstValue(
    service.noAddressReason,
    service.no_address_reason,
    business?.noAddressReason,
    business?.no_address_reason
  ) || '').trim();
  if (noAddressReason) return false;

  const address = cleanAddress(firstValue(
    business?.address,
    [business?.streetAddress, business?.street_address, business?.city, business?.state, business?.zip].filter(Boolean).join(', ')
  ));
  if (!looksLikeStreetAddress(address)) return false;

  const serviceArea = String(firstValue(service.area, service.serviceArea, service.service_area, business?.serviceArea, business?.service_area) || '').trim();
  return !serviceArea || serviceArea.toLowerCase() !== address.toLowerCase();
}

function looksLikeStreetAddress(address) {
  const value = String(address || '').trim();
  return /\d/.test(value) && !/^(online|service[-\s]?based|virtual)\b/i.test(value);
}

async function hydrateCachedVenueInfo() {
  const roots = [...document.querySelectorAll('[data-video-slug][data-api-base]')]
    .filter((root) => root.tagName.toLowerCase().startsWith('venue-info-') && !root.dataset.businessSlug);
  await Promise.allSettled(roots.map(async (root) => {
    const slug = root.dataset.videoSlug || root.dataset.businessSlug || '';
    const base = (root.dataset.apiBase || '/apps/so-atlanta/api/v1').replace(/\/+$/, '');
    if (!slug) return;

    const response = await fetch(`${base}/listings/${encodeURIComponent(slug)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    if (!response.ok) return;
    const payload = await response.json();
    const business = payload?.listing || payload?.business || payload;
    if (!business || typeof business !== 'object') return;

    const name = firstValue(business.name, business.title);
    const title = root.querySelector('[class*="ai-venue-info__title-"]');
    if (name && title instanceof HTMLElement) title.textContent = name;

    const description = firstValue(
      business.shortDescription,
      business.short_description,
      business.description,
      business.longDescription,
      business.long_description
    );
    const descriptionEl = root.querySelector('[class*="ai-venue-info__description-"]');
    if (descriptionEl instanceof HTMLElement) {
      const html = textHtml(description);
      descriptionEl.innerHTML = html;
      descriptionEl.hidden = !html;
    }

    const lat = Number(firstValue(business.latitude, business.lat));
    const lng = Number(firstValue(business.longitude, business.lng));
    const address = cleanAddress(firstValue(business.address, [business.city, business.state, business.zip].filter(Boolean).join(', ')));
    const href = firstValue(business.googleMapsUrl, business.google_maps_url) || mapsUrl(name, address, lat, lng);
    renderHeaderAddress(root, business, address, href, lat, lng);

    setInfoCard(root, 'Address', address ? `<p><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(address)}</a></p>` : '');
    setInfoCard(root, 'Hours', textHtml(firstValue(business.hoursText, business.hours_text, business.hours)));
    setInfoCard(root, 'Contact', renderContact(business));
    setInfoCard(root, 'Amenities', textHtml(firstValue(business.amenities, business.amenityNotes, business.amenity_notes)));
    setInfoCard(root, 'Parking', textHtml(firstValue(business.parkingNotes, business.parking_notes, business.parking)));
    setInfoCard(root, 'Accessibility', textHtml(firstValue(business.accessibilityNotes, business.accessibility_notes, business.accessibility)));
  }));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateCachedVenueInfo, { once: true });
} else {
  hydrateCachedVenueInfo();
}
